import React, { useState, useEffect, useRef } from "react";
import { storage, db, auth } from "../firebase";
import {
  doc,
  updateDoc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { HexColorPicker } from "react-colorful";


function Build() {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const location = useLocation();

  const isBuild = location.pathname === "/build";
  const isCustomize = location.pathname.includes("/customize");

  const [spaceName, setSpaceName] = useState("");
  const [spaceLink, setSpaceLink] = useState("");

  const [bio, setBio] = useState("");
  const [cover, setCover] = useState("");
  const [overlay, setOverlay] = useState("none");
  const [buttonText, setButtonText] = useState("Enter space");
  const [music, setMusic] = useState("");

  const [bgImage, setBgImage] = useState(null);
  const [bgPreview, setBgPreview] = useState("");
  const [existingBg, setExistingBg] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [bgMode, setBgMode] = useState("image");
  const [buttonColor, setButtonColor] = useState("#696969"); // background
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff"); // text

  const [showButtonColorPicker, setShowButtonColorPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [buttonPosition, setButtonPosition] = useState("bottom");
  const [showPositionPicker, setShowPositionPicker] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCoverOptions, setShowCoverOptions] = useState(false);

  const [oldLink, setOldLink] = useState("");
  const storage = getStorage();
  const [coverPreview, setCoverPreview] = useState("");
  const [linkStatus, setLinkStatus] = useState("");
  const [checking, setChecking] = useState(false);
  const RESERVED_LINKS = ["home", "engage", "yard", "create", "update", "build"];
  const STATUS = {
  idle: "",
  checking: "checking",
  available: "available",
  taken: "taken",
  reserved: "reserved",
};
  const isReservedRef = useRef(false);

const normalizeLink = (value) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")     // spaces → dash FIRST
    .replace(/[^a-z0-9-]/g, "") // remove invalid chars
    .replace(/-+/g, "-")       // collapse multiple dashes
    .replace(/^-|-$/g, "");    // remove leading/trailing dash

const isReserved = (value) => RESERVED_LINKS.includes(value);

    // 🖼 BACKGROUND IMAGE
const handleBgImage = async (e) => {
  const file = e.target.files[0];
  if (!file || !auth.currentUser) return;

  try {
    // 1. LOCAL PREVIEW (instant UI feedback)
    setBgImage(file);
    setBgPreview(URL.createObjectURL(file));
    setBgMode("image");
    setBgColor("#ffffff");

    // 2. UPLOAD TO FIREBASE STORAGE
    const storageRef = ref(
      storage, `covers/${user.uid}/${Date.now()}`);

await uploadBytes(storageRef, file);

    // 3. GET DOWNLOAD URL
    const url = await getDownloadURL(storageRef);

  setCover(url);
  setCoverPreview(url); // now works correctly

    // 4. SAVE TO FIRESTORE (THIS IS WHAT SPACE READS)
await updateDoc(doc(db, "spaces", user.uid), {
  cover: url,
});

    // optional sync local state
    setExistingBg(url);

  } catch (err) {
    console.error("Background upload failed:", err);
  }
};

const handleSelectColorMode = () => {
  setBgMode("color");

  // remove image when switching to color
  setBgImage(null);
  setBgPreview("");
};

const handleColorChange = (color) => {
  setBgColor(color);
  setBgMode("color");

  setBgImage(null);
  setBgPreview("");
};

  /* ================= LOAD SPACE ================= */
  useEffect(() => {
    const load = async () => {
      if (!user) return;

      const ref = doc(db, "spaces", user.uid);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();

        setSpaceName(data.spaceName || "");
        setSpaceLink(data.spaceLink || "");
        setOldLink(data.spaceLink || "");

        setBio(data.bio || "");
        setCover(data.cover || "");
        setOverlay(data.overlay || "none");
        setButtonText(data.buttonText || "Enter space");
        setButtonColor(data.buttonColor || "#696969");
        setButtonTextColor(data.buttonTextColor || "#ffffff");
        setMusic(data.music || "");
        setBgMode(data.bgMode || "image");          // ✅ IMPORTANT
        setBgColor(data.bgColor || "#ffffff");     // ✅ IMPORTANT
        setButtonPosition(data.buttonPosition || "bottom"); // ✅ IMPORTANT
        setExistingBg(data.background || "");      // ✅ IMPORTANT
        setBgPreview(data.background || "");       // ✅ show preview

        if (data.background || data.bgMode === "color") {
        setShowCoverOptions(true);
        
      }
      }

      setLoading(false);
    };

    load();
  }, [user]);

const checkIdRef = useRef(0);
const debounceRef = useRef(null);

const checkAvailability = async (link) => {
  const clean = normalizeLink(link);

  if (!clean) {
    setLinkStatus("");
    return;
  }

  // 🚨 HARD BLOCK (FINAL AUTHORITY)
  if (RESERVED_LINKS.includes(clean)) {
    setLinkStatus("reserved");
    return;
  }

  setLinkStatus("checking");

  const q = query(
    collection(db, "spaces"),
    where("spaceLink", "==", clean)
  );

  const snap = await getDocs(q);

  const taken =
    !snap.empty &&
    snap.docs[0].data().userId !== user.uid;

  setLinkStatus(taken ? "taken" : "available");
};

  /* ================= CHECK LINK ================= */
  const isLinkTaken = async (link) => {
    const q = query(
      collection(db, "spaces"),
      where("spaceLink", "==", link)
    );

    const snap = await getDocs(q);

    if (snap.empty) return false;

    return snap.docs[0].data().userId !== user.uid;
  };

  /* ================= SAVE ================= */
const handleSave = async () => {
  if (!user) return;

  const cleanLink = normalizeLink(spaceLink);

  if (!cleanLink) {
    setError("Space link is required");
    return;
  }

  if (isReserved(cleanLink)) {
    setError("This username is not allowed");
    return;
  }

  if (!spaceName.trim()) {
    setError("Space name is required");
    return;
  }

  setLoading(true);
  setError("");

  try {
    const linkChanged = oldLink !== cleanLink;

    if (!isCustomize || linkChanged) {
      const taken = await isLinkTaken(cleanLink);

      if (taken || isReserved(cleanLink)) {
        setError("Space link already taken");
        setLoading(false);
        return;
      }
    }

    await setDoc(doc(db, "spaces", user.uid), {
      userId: user.uid,
      ownerUid: user.uid,
      spaceName,
      spaceLink: cleanLink,
      bio,
      cover,
      background: existingBg || cover, // ✅ important
      overlay,
      buttonText,
      buttonColor,
      buttonTextColor,
      buttonPosition,
      bgMode,
      bgColor,
      music,
      updatedAt: Date.now()
    }, { merge: true });

    await setDoc(doc(db, "users", user.uid), {
      spaceName,
      spaceLink: cleanLink
    }, { merge: true });

    setOldLink(cleanLink);
    navigate(`/${cleanLink}`);
  } catch (err) {
    console.error(err);
    setError("Failed to save space");
  }

  setLoading(false);
};



  if (loading) return <div style={styles.center}>Loading...</div>;



  const cleanLink = normalizeLink(spaceLink);

  const isSameLink = normalizeLink(spaceLink) === oldLink;

  const isLinkAllowed = (link) => {
  if (isReserved(link)) return false;
  if (linkStatus === "taken") return false;
  return true;
};

const canBuild =
  spaceName.trim() !== "" &&
  cleanLink !== "" &&
  (
    isCustomize
      ? (isSameLink || isLinkAllowed(cleanLink))
      : isLinkAllowed(cleanLink)
  );


  return (
    <div style={styles.page}>
      <div style={styles.card}>

<h2 style={styles.title}>
  {isCustomize ? "Customize your space" : "Build your space"}
</h2>

{(isBuild || isCustomize) && (
  <button
    onClick={() => {
      if (isBuild) {
        navigate("/engage");
      }

      if (isCustomize) {
        const linkToUse = oldLink || spaceLink;
        navigate(`/${linkToUse}`);
      }
    }}
    style={styles.closePageButton}
  >
    ×
  </button>
)}

<p style={styles.minilabel}>Space link:</p>
<div style={styles.fieldWrap}>

  {/* VISUAL URL PREFIX */}
  {spaceLink && (
    <p style={styles.urlPrefix}>
      https://heydiari.com/
      <span style={{ fontWeight: "600" }}>{spaceLink}</span>
    </p>
  )}

  <input
    placeholder="Enter your space link"
    value={spaceLink}
onChange={(e) => {
  let value = e.target.value.toLowerCase();

  value = value.replace(/\s+/g, "-");
  value = value.replace(/[^a-z0-9-]/g, "");

  setSpaceLink(value);

  // EMPTY RESET
  if (!value) {
    setLinkStatus("");
    isReservedRef.current = false;
    return;
  }

  // RESERVED LOCK
  if (RESERVED_LINKS.includes(value)) {
    isReservedRef.current = true;
    setLinkStatus("reserved");
    return;
  }

  isReservedRef.current = false;

  clearTimeout(debounceRef.current);

  setLinkStatus("checking");

  debounceRef.current = setTimeout(() => {
    if (!isReservedRef.current) {
      checkAvailability(value);
    }
  }, 300);
}}
    style={styles.input}
  />

  {/* STATUS TEXT */}
{spaceLink.trim().length > 0 && linkStatus && (
  <p style={{
    fontSize: "12px",
    marginTop: "-6px",
    color:
      linkStatus === "taken"
        ? "#ff0d00"
        : linkStatus === "available"
        ? "#00b92e"
        : linkStatus === "reserved"
        ? "#ff6a00"
        : linkStatus === "checking"
        ? "#0049b7"
        : "#ddd"
  }}>
    {linkStatus === "checking" && "Checking availability..."}
    {linkStatus === "taken" && "Space link not available"}
    {linkStatus === "available" && "Space link available"}
    {linkStatus === "reserved" && "Space link is not allowed"}
  </p>
)}
</div>

<p style={styles.minilabel}>Space name:</p>
<div style={styles.fieldWrap}>
  <input
    placeholder="Enter your space name"
    value={spaceName}
    onChange={(e) => setSpaceName(e.target.value)}
    style={styles.input}
  />
</div>

<label style={{ margin: "30px auto 20px", cursor: "pointer" }}>
  <input
    type="checkbox"
    checked={showCoverOptions}
    onChange={(e) => setShowCoverOptions(e.target.checked)}
    style={{ marginRight: "8px" }}
  />
  Add space cover
</label>

{showCoverOptions && (
  <>
<div style={styles.bgWrap}>
  <p style={styles.label}>Choose a wallpaper for your space cover:</p>

{showCoverOptions ? (
<div style={styles.bgPreviewWrap}>


  {/* IMAGE MODE */}
  {bgMode === "image" && bgPreview && (
    <img src={bgPreview} style={styles.bgBox} />
  )}

<button
  style={{
    position: "absolute",
    left: "50%",
    transform:
      buttonPosition === "center"
        ? "translate(-50%, -50%)"
        : "translateX(-50%)",

    top:
      buttonPosition === "top"
        ? "15px"
        : buttonPosition === "center"
        ? "50%"
        : "auto",

    bottom:
      buttonPosition === "bottom"
        ? "15px"
        : "auto",

    padding: "3px 8px",
    borderRadius: "20px",
    border: "none",
    background: buttonColor,
    color: buttonTextColor,
    fontSize: "4px",
    zIndex: 5,
    cursor: "pointer"
  }}
>
  {buttonText}
</button>

  {/* COLOR MODE */}
  {bgMode === "color" && (
    <div
      style={{
        ...styles.bgBox,
        background: bgColor
      }}
    />
  )}

  {/* EMPTY STATE */}
  {bgMode === "image" && !bgPreview && (
    <div style={styles.bgPlaceholder}>
      <div style={styles.bgLines}>
        <div style={styles.bgLine}></div>
        <div style={styles.bgLine}></div>
      </div>
    </div>
  )}

  {/* OVERLAY */}
  {overlay !== "none" && (
    <div
      style={{
        ...styles.bgOverlay,
        background:
          overlay === "light"
            ? "rgba(255,255,255,0.4)"
            : "rgba(0,0,0,0.4)"
      }}
    />
  )}

  <div style={styles.bgLinesOverlay}>
    <div style={styles.bgLine}></div>
    <div style={styles.bgLine}></div>
  </div>

</div>
) : (
  <div style={styles.bgPlaceholder}>
    <div style={styles.bgLines}>
      <div style={styles.bgLine}></div>
      <div style={styles.bgLine}></div>
    </div>
  </div>
)}

  <input
    type="file"
    id="bgUpload"
    onChange={handleBgImage}
    style={{ display: "none" }}
  />

<div style={styles.bgButtonRow}>

  <label htmlFor="bgUpload" style={styles.fileButton}>
    {bgMode === "image" && (bgImage || bgPreview || existingBg)
  ? "Change image"
  : "Select image"}
  </label>

  <button
    type="button"
    onClick={() => setShowColorPicker(true)}
    style={styles.fileButton}
  >
    {bgMode === "color"
  ? "Change color"
  : "Select color"}
  </button>

{showColorPicker && (
  <div
    style={styles.modalOverlay}
    onClick={() => setShowColorPicker(false)}
  >
    <div
      style={styles.modalBox}
      onClick={(e) => e.stopPropagation()}
    >
      <button
  onClick={() => setShowColorPicker(false)}
  style={styles.closeButton}
>
  ×
</button>

      <p style={styles.modalTitle}>Select color</p>

      <div style={styles.pickerWrap}>
<HexColorPicker
  color={bgColor}
  onChange={(color) => {
    setBgColor(color);
    setBgMode("color");   // 🔥 FORCE MODE
    setBgImage(null);
    setBgPreview("");
  }}
/>
      </div>

<input
  value={bgColor}
  onChange={(e) => {
    setBgColor(e.target.value);
    setBgMode("color");
    setBgImage(null);
    setBgPreview("");
  }}
  style={styles.hexInput}
/>

      <button
        onClick={() => setShowColorPicker(false)}
        style={styles.doneButton}
      >
        Select
      </button>

    </div>
  </div>
)}
</div>
</div>


        {/* OVERLAY */}
        <div>
          <p style={styles.overlaylabel}>Select an overlay to your wallpaper:</p>

<div style={styles.overlayWrap}>
  <div
    onClick={() => setOverlay("none")}
    style={overlay === "none" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    None
  </div>

  <div
    onClick={() => setOverlay("light")}
    style={overlay === "light" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    White
  </div>

  <div
    onClick={() => setOverlay("dark")}
    style={overlay === "dark" ? styles.activeOverlayBtn : styles.overlayBtn}
  >
    Black
  </div>
  
</div>
        </div>

                {/* MUSIC */}
        <p style={styles.minilabel}>Youtube link for music:</p>
    <input
    placeholder="Enter youtube link"
    value={music}
    onChange={(e) => setMusic(e.target.value)}
    style={styles.input}
        />


        {/* BUTTON TEXT */}
        <p style={styles.minilabel}>Button text:</p>
        <input
        placeholder="Enter space"
          value={buttonText}
          onChange={(e) => setButtonText(e.target.value)}
          style={styles.input}
        />

<div style={styles.colorRow}>
  <button
    type="button"
    onClick={() => setShowButtonColorPicker(true)}
    style={styles.fileButton}
  >
    Button Color
  </button>

  <button
    type="button"
    onClick={() => setShowTextColorPicker(true)}
    style={styles.fileButton}
  >
    Text Color
  </button>

<button
  type="button"
  onClick={() => setShowPositionPicker(true)}
  style={styles.fileButton}
>
  Position
</button>
</div>


{showButtonColorPicker && (
  <div
    style={styles.modalOverlay}
    onClick={() => setShowButtonColorPicker(false)}
  >
    <div
      style={styles.modalBox}
      onClick={(e) => e.stopPropagation()}
    >
      <button
  onClick={() => setShowButtonColorPicker(false)}
  style={styles.closeButton}
>
  ×
</button>

      <p style={styles.modalTitle}>Select color</p>

      <div style={styles.pickerWrap}>
<HexColorPicker
  color={buttonColor}
  onChange={(color) => {
    setButtonColor(color);
  }}
/>
      </div>

<input
  value={buttonColor}
  onChange={(e) => {
    setButtonColor(e.target.value);
  }}
  style={styles.hexInput}
/>

      <button
        onClick={() => setShowButtonColorPicker(false)}
        style={styles.doneButton}
      >
        Select
      </button>

    </div>
  </div>
)}




{showTextColorPicker && (
  <div
    style={styles.modalOverlay}
    onClick={() => setShowTextColorPicker(false)}
  >
    <div
      style={styles.modalBox}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setShowTextColorPicker(false)}
        style={styles.closeButton}
      >
        ×
      </button>

      <p style={styles.modalTitle}>Select text color</p>

      <div style={styles.pickerWrap}>
        <HexColorPicker
          color={buttonTextColor}
          onChange={setButtonTextColor}
        />
      </div>

      <input
        value={buttonTextColor}
        onChange={(e) => setButtonTextColor(e.target.value)}
        style={styles.hexInput}
      />

      <button
        onClick={() => setShowTextColorPicker(false)}
        style={styles.doneButton}
      >
        Select
      </button>
    </div>
  </div>
)}


{showPositionPicker && (
  <div
    style={styles.modalOverlay}
    onClick={() => setShowPositionPicker(false)}
  >
    <div
      style={styles.modalBox}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setShowPositionPicker(false)}
        style={styles.closeButton}
      >
        ×
      </button>

      <p style={styles.modalTitle}>Button Position</p>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", alignItems: "center"}}>
        
        <button
          onClick={() => {
            setButtonPosition("top");
            setShowPositionPicker(false);
          }}
          style={{
            ...styles.doneButton,
            background: buttonPosition === "top" ? "#696969" : "#eee",
            color: buttonPosition === "top" ? "#ffffff" : "#000"
          }}
        >
          Top
        </button>

        <button
          onClick={() => {
            setButtonPosition("center");
            setShowPositionPicker(false);
          }}
          style={{
            ...styles.doneButton,
            background: buttonPosition === "center" ? "#696969" : "#eee",
            color: buttonPosition === "center" ? "#ffffff" : "#000"
          }}
        >
          Center
        </button>

        <button
          onClick={() => {
            setButtonPosition("bottom");
            setShowPositionPicker(false);
          }}
          style={{
            ...styles.doneButton,
            background: buttonPosition === "bottom" ? "#696969" : "#eee",
            color: buttonPosition === "bottom" ? "#ffffff" : "#000"
          }}
        >
          Bottom
        </button>

      </div>
    </div>
  </div>
)}

</>
)}

<button
  onClick={handleSave}
  disabled={!canBuild || loading}
  style={{
    ...styles.button,
    opacity: loading ? 0.5 : canBuild ? 1 : 0.5,
    cursor: loading ? "not-allowed" : canBuild ? "pointer" : "not-allowed"
  }}
>
{isCustomize ? "Customize" : "Build"}

</button>
      </div>
    </div>
  );
}

export default Build;

const styles = {
  center: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  page: {
    height: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    overflowY: "auto",
    backgroundColor: "rgba(255, 255, 255, 0.4)",
  },

card: {
  backgroundColor: "rgba(255, 255, 255, 0.4)",
  position: "relative",
  paddingTop: "30px",
  paddingBottom: "30px",
  width: "100%",
  maxWidth: "800px",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",   // 🔥 center horizontally
  textAlign: "center",    // 🔥 center text
  fontFamily: "'Inter', sans-serif",
  color: "#000",
  margin: "0px auto 20px"
},

title: {
  fontSize: "25px",
  fontWeight: "600",
  marginBottom: "40px",
  letterSpacing: "0.3px",
  color: "#000",
  fontFamily: "'Inter', sans-serif",
},

  linkRow: {
    display: "flex",
    alignItems: "center",
    gap: "5px"
  },

  domain: {
    fontSize: "14px",
    color: "#888"
  },

  closePageButton: {
  position: "absolute",
  top: "15px",
  right: "15px",
  background: "transparent",
  border: "none",
  fontSize: "26px",
  cursor: "pointer",
  color: "#555",
},

  input: {
  width: "100%",
  maxWidth: "310px",
  padding: "14px 16px",
  marginBottom: "12px",
  borderRadius: "15px",
  border: "1px solid #ccc",
  textAlign: "center",
  fontSize: "15px",
  fontFamily: "'Inter', sans-serif",
  boxSizing: "border-box",
  backgroundColor: "rgba(255, 255, 255, 0.4)",
},

  label:{
  fontSize:"15px",
  margin: "0px auto 15px",
  color: "#000",
},

  minilabel:{
  fontSize:"12px",
  margin: "0px auto 5px",
  color: "#000",
  },

  overlaylabel:{
  fontSize:"15px",
  margin: "55px auto 15px",
  color: "#000",
},

  textarea: {
    padding: "10px",
    minHeight: "80px"
  },

  preview: {
    width: "100%",
    height: "140px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: "10px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

overlayWrap:{display:"flex",justifyContent:"center",gap:"15px",margin:"0 auto 40px"},

overlayBtn: {
  padding: "8px 20px",
  borderRadius: "20px",
  background: "#eee",
  cursor: "pointer",
  fontSize: "0.85rem",
  color: "#000",
  textAlign: "center",
  transition: "0.2s",
},

activeOverlayBtn: {
  padding: "8px 20px",
  borderRadius: "20px",
  background: "#696969",
  color: "#fff",
  cursor: "pointer",
  fontSize: "0.85rem"
},

fileButton: {
  display: "inline-block",
  padding: "10px 16px",
  borderRadius: "20px",
  background: "#eee",
  cursor: "pointer",
  fontSize: "0.85rem",
  color: "#000",
  margin:"0 auto 0",
  border: "none",        // ✅ removes black border
  outline: "none"     
},

bgWrap: {
  width: "100%",
  maxWidth: "310px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "-15px",
},

bgPreviewWrap: {
  width: "100%",
  height: "180px",
  position: "relative",
  overflow: "hidden",   // 🔥 IMPORTANT FIX
  borderRadius: "15px",
  margin: "0 auto 15px",
},

bgBox: {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  position: "absolute",
  inset: 0,
},

bgOverlay: {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 2,
  transition: "0.25s ease",
  position: "absolute",
  inset: 0,
  zIndex: 2,
},

bgPlaceholder: {
  width: "100%",
  height: "180px",
  background: "#eee",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin:"0 auto 15px",
  position: "relative",
  borderRadius: "15px",
},

bgLines: {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "120px",
  height: "100%",   // 🔥 IMPORTANT (fills the box)
  width: "100%"
},

bgLine: {
  width: "0px", // 🔥 important (we use border instead)
  height: "100%",
  borderLeft: ".5px dotted #999",
  opacity: 0.8
},

bgLinesOverlay: {
  position: "absolute",
  inset: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "120px",
  zIndex: 3,
  pointerEvents: "none"
},

bgButtonRow: {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
  alignItems: "center"
},

modalOverlay: {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999
},

modalBox: {
  width: "250px",
  background: "#fff",
  padding: "20px",
  borderRadius: "15px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  textAlign: "center",
  position: "relative" // 🔥 REQUIRED
},

modalTitle: {
  margin: 0,
  fontSize: "16px",
  fontWeight: "600",
  textAlign: "center"
},

pickerWrap: {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%"
},

hexInput: {
  width: "40%",
  textAlign: "center",
  fontFamily: "monospace",
  padding: "10px",
  borderRadius: "15px",
  border: "1px solid #ccc",
  outline: "none",
  display: "block"
},

doneButton: {
  width: "65%",
  padding: "12px",
  borderRadius: "15px",
  border: "none",
  background: "#696969",
  color: "#fff",
  cursor: "pointer"
},

closeButton: {
  position: "absolute",
  top: "10px",
  right: "12px",
  background: "transparent",
  border: "none",
  fontSize: "20px",
  cursor: "pointer",
  color: "#555"
},

  previewText: {
    position: "relative",
    color: "#fff",
    fontWeight: "bold",
    zIndex: 1
  },

  fieldWrap: {
  width: "100%",
  maxWidth: "420px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative"
},

urlPrefix: {
  fontSize:"12px",
  margin: "0px auto 5px",
  color: "#666",
  letterSpacing: "0.2px",
},

colorRow: {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "25px",
},

button:{marginTop:"20px",padding:"15px",width:"100%",maxWidth:"290px",borderRadius:"25px",border:"none",background:"#696969",color:"#fff"},

};